/**
 * Parse a datetime string from the backend as UTC, even when it lacks a 'Z'
 * suffix (SQLite stores datetimes as "YYYY-MM-DD HH:MM:SS" without timezone).
 * Strings that already carry offset info are passed through unchanged.
 */
export function parseUTC(s) {
  if (!s) return null;
  if (/[Zz]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
  return new Date(s.replace(" ", "T") + "Z");
}

/**
 * The IANA zone to render in: the archer's configured `tz` when they have one,
 * otherwise the browser's. The one definition of that fallback — it was written
 * out identically in five functions in this file, and #2211 needed a SIXTH
 * caller (Practice, which must send the resolved zone to the server rather than
 * just format with it, because the server cannot see the browser's).
 *
 * Export it rather than keeping it private precisely because of that class of
 * caller: anything that has to NAME the zone — a query param, a stored
 * preference, a label — must resolve it the same way the rendering does, or the
 * value shown and the value sent disagree.
 */
export function resolveTimeZone(tz) {
  return tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function formatDate(isoString, tz, opts = {}) {
  if (!isoString) return "";
  const timeZone = resolveTimeZone(tz);
  return parseUTC(isoString).toLocaleDateString("en-US", { timeZone, ...opts });
}

export function formatDateTime(isoString, tz, opts = {}) {
  if (!isoString) return "";
  const timeZone = resolveTimeZone(tz);
  return parseUTC(isoString).toLocaleString("en-US", { timeZone, ...opts });
}

export function formatShortDate(isoString, tz) {
  return formatDate(isoString, tz, { month: "short", day: "numeric", year: "numeric" });
}

export function formatMonthDay(isoString, tz) {
  return formatDate(isoString, tz, { month: "short", day: "numeric" });
}

/**
 * Uppercase day+month+year — "MAY 10, 2026". The card-header date style
 * (#2068), previously a private `shortDate` in RoundSummaryCard.jsx.
 *
 * NB the comment on that original read "Format an ISO date as e.g. '18 MAY
 * 2026'", which is not what it produced: en-US puts the month first and adds a
 * comma regardless of the option order. Output is unchanged here — only the
 * description is corrected.
 */
export function formatDayMonthYearUpper(isoString, tz) {
  return formatDate(isoString, tz, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).toUpperCase();
}

/**
 * Uppercase month+year, e.g. "MAY 2026". The History month-group heading
 * (#2068) — previously a *second*, differently-shaped `shortDate` in
 * History.jsx that collided by name with RoundSummaryCard's.
 */
export function formatMonthYearUpper(isoString, tz) {
  return formatDate(isoString, tz, { month: "short", year: "numeric" }).toUpperCase();
}

/**
 * Uppercase month+year for a plain "YYYY-MM-DD" calendar day (no timestamp,
 * no timezone conversion). Arrow logs store a bare local date, so parsing it
 * as UTC would shift the month at the boundary; this builds the Date locally
 * instead. Output matches formatMonthYearUpper exactly so logs and rounds
 * land in the same History group (#2068).
 */
export function formatMonthYearUpperFromDay(dateStr) {
  const [y, m] = String(dateStr || "").split("-").map(Number);
  if (!y || !m) return "";
  return new Date(y, m - 1, 1)
    .toLocaleDateString("en-US", { month: "short", year: "numeric" })
    .toUpperCase();
}

/**
 * Month + day for a plain "YYYY-MM-DD" calendar day, e.g. "18 May" → "May 18".
 * Like formatMonthYearUpperFromDay this is for bare date strings, not
 * timestamps: the "T00:00:00" suffix (no Z) makes it a LOCAL midnight, so the
 * day never shifts. Previously duplicated in PracticeStreakCard.jsx and
 * ShotCalendar.jsx (#2068).
 */
export function formatMonthDayFromDay(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * The AMBIENT-LOCAL calendar day of a Date as "YYYY-MM-DD".
 *
 * Built from the local getters, never `toISOString()`: `toISOString()` renders
 * in UTC, so east of Greenwich local midnight is still the previous day and
 * the result comes back one day early. That is shape B in the header of
 * src/timezone.test.js, and it is what #2160 was — three independent
 * re-implementations of this helper, two of them wrong in exactly that way.
 * This is now the single definition; it moved here from ArrowLogSheet.jsx so
 * the four sites could share it rather than each growing their own.
 *
 * Use this for a day that means "the day the user is standing in" — a date
 * input's `max`, the default value of a date field, the start/end of "this
 * month". For the local day OF A SERVER TIMESTAMP use `localDayKey`, which
 * takes the archer's configured `tz` and does not depend on the browser zone.
 */
export function localISODate(d = new Date()) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Local calendar day of a UTC timestamp as a sortable "YYYY-MM-DD" key,
 * resolved with the same timezone semantics as formatDate: the given `tz`
 * when set, otherwise the browser timezone. (en-CA formats as ISO order.)
 */
export function localDayKey(isoString, tz) {
  if (!isoString) return "";
  return parseUTC(isoString).toLocaleDateString("en-CA", {
    timeZone: resolveTimeZone(tz),
  });
}

/**
 * Convert a UTC ISO string to a "YYYY-MM-DDTHH:mm" local-time string suitable
 * for use as the value of a <input type="datetime-local"> rendered in `tz`.
 */
export function toLocalInputValue(isoString, tz) {
  const d = parseUTC(isoString);
  if (!d) return "";
  return d
    .toLocaleString("sv-SE", {
      timeZone: resolveTimeZone(tz),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(" ", "T");
}

/**
 * Convert a "YYYY-MM-DDTHH:mm" string (from a datetime-local input rendered in
 * `tz`) back to a UTC ISO string.  The wall-clock in `tz` is treated as the
 * intended local time.
 */
export function fromLocalInputValue(localValue, tz) {
  if (!localValue) return null;
  const zone = resolveTimeZone(tz);
  const [date, time] = localValue.split("T");
  const [Y, Mo, D] = date.split("-").map(Number);
  const [H, Mi] = time.split(":").map(Number);
  // First guess: pretend the wall-clock is UTC, then measure offset.
  const guess = Date.UTC(Y, Mo - 1, D, H, Mi);
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
      .formatToParts(new Date(guess))
      .map((p) => [p.type, p.value])
  );
  const hour = +parts.hour === 24 ? 0 : +parts.hour;
  const tzWall = Date.UTC(+parts.year, +parts.month - 1, +parts.day, hour, +parts.minute);
  return new Date(guess + (guess - tzWall)).toISOString();
}
